/**
 * UserInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { FileInfo } from './file_info'

/**
 * UserInfo
 */
export interface UserInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** 禁用状态 */
  disabled?: boolean
  /** 禁用时间 */
  disabled_at?: string
  /** 禁用截止时间（null表示永久禁用） */
  disabled_until?: string
  /** 禁用原因 */
  disabled_reason?: string
  /** 用户名，全局唯一，不可重复 */
  username?: string
  /** 昵称，可重复 */
  nickname?: string
  /** 头像文件ID */
  avatar_id?: string
  /** 头像文件信息 */
  avatar?: FileInfo
  /** 背景文件ID */
  background_id?: string
  /** 背景文件信息 */
  background?: FileInfo
  /** 用户性别（可选值：unknown=未知; male=男性; female=女性; secret=保密; other=其他） */
  gender?: string
  /** 生日（UTC时间） */
  birthday?: string
  /** 个人简介 */
  bio?: string
  /** 用户类型（可选值：real=真实用户; guest=访客用户; robot=机器人用户; bot=智能体用户） */
  type?: string
  /** 是否设置了密码 */
  has_password?: boolean
  /** 智能体ID（type=bot时有值） */
  bot_id?: string
  /** 最后登录时间（UTC） */
  last_login_at?: string
  /** 用户等级积分，可以计算出rank_tier、rank_grade、rank_stars */
  rank_points?: number
  /** 用户段位等级（可选值：0=无段位; 1=青铜; 2=白银; 3=黄金; 4=铂金; 5=钻石; 6=星耀; 7=最强王者） */
  rank_tier?: number
  /** 用户段位等级（可选值：0; 1=Ⅰ; 2=Ⅱ; 3=Ⅲ; 4=Ⅳ; 5=Ⅴ; 6=Challenger） */
  rank_division?: number
  /** 用户段位等级星数 */
  rank_stars?: number
  /** 用户当前段位最大星数 */
  rank_max_stars?: number
  /** 用户在线状态（可选值：offline=离线; online=在线; push_online=推送在线） */
  online_state?: string
  /** 在线状态更新时间（UTC） */
  online_state_updated_at?: string
  /** 当前登录设备ID（关联 device_info.id） */
  device_id?: string
  /** 同步状态（可选值：pending=待同步; synced=已同步; failed=同步失败） */
  stream_sync_status?: string
  /** Stream.io同步错误信息 */
  stream_sync_error?: string
  /** Stream.io同步时间 */
  stream_synced_at?: string
}

/**
 * UserInfo Schema
 */
export const UserInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    disabled: {
      description: '禁用状态',
      format: 'bool',
      type: 'boolean',
      label: '禁用状态',
    },
    disabled_at: {
      description: '禁用时间',
      format: '*time.Time',
      type: 'string',
      label: '禁用时间',
    },
    disabled_until: {
      description: '禁用截止时间（null表示永久禁用）',
      format: '*time.Time',
      type: 'string',
      label: '禁用截止时间',
    },
    disabled_reason: {
      description: '禁用原因',
      format: 'string',
      type: 'string',
      label: '禁用原因',
    },
    username: {
      description: '用户名，全局唯一，不可重复',
      format: 'string',
      type: 'string',
      label: '用户名',
    },
    nickname: {
      description: '昵称，可重复',
      format: 'string',
      type: 'string',
      label: '昵称',
    },
    avatar_id: {
      description: '头像文件ID',
      format: 'string',
      type: 'string',
      label: '头像文件ID',
    },
    avatar: {
      $ref: 'FileInfo',
      description: '头像文件信息',
      label: '头像文件信息',
    },
    background_id: {
      description: '背景文件ID',
      format: 'string',
      type: 'string',
      label: '背景文件ID',
    },
    background: {
      $ref: 'FileInfo',
      description: '背景文件信息',
      label: '背景文件信息',
    },
    gender: {
      description:
        '用户性别（可选值：unknown=未知; male=男性; female=女性; secret=保密; other=其他）',
      format: 'cons.UserGender',
      type: 'string',
      label: '用户性别',
    },
    birthday: {
      description: '生日（UTC时间）',
      format: '*time.Time',
      type: 'string',
      label: '生日',
    },
    bio: {
      description: '个人简介',
      format: 'string',
      type: 'string',
      label: '个人简介',
    },
    type: {
      description:
        '用户类型（可选值：real=真实用户; guest=访客用户; robot=机器人用户; bot=智能体用户）',
      format: 'cons.UserType',
      type: 'string',
      label: '用户类型',
    },
    has_password: {
      description: '是否设置了密码',
      format: 'bool',
      type: 'boolean',
      label: '是否设置了密码',
    },
    bot_id: {
      description: '智能体ID（type=bot时有值）',
      format: 'string',
      type: 'string',
      label: '智能体ID',
    },
    last_login_at: {
      description: '最后登录时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '最后登录时间',
    },
    rank_points: {
      description: '用户等级积分，可以计算出rank_tier、rank_grade、rank_stars',
      format: 'int',
      type: 'integer',
      label: '用户等级积分',
    },
    rank_tier: {
      description:
        '用户段位等级（可选值：0=无段位; 1=青铜; 2=白银; 3=黄金; 4=铂金; 5=钻石; 6=星耀; 7=最强王者）',
      format: 'cons.UserRankTier',
      type: 'integer',
      label: '用户段位等级',
    },
    rank_division: {
      description: '用户段位等级（可选值：0; 1=Ⅰ; 2=Ⅱ; 3=Ⅲ; 4=Ⅳ; 5=Ⅴ; 6=Challenger）',
      format: 'cons.UserRankDivision',
      type: 'integer',
      label: '用户段位等级',
    },
    rank_stars: {
      description: '用户段位等级星数',
      format: 'int',
      type: 'integer',
      label: '用户段位等级星数',
    },
    rank_max_stars: {
      description: '用户当前段位最大星数',
      format: 'int',
      type: 'integer',
      label: '用户当前段位最大星数',
    },
    online_state: {
      description: '用户在线状态（可选值：offline=离线; online=在线; push_online=推送在线）',
      format: 'cons.UserOnlineState',
      type: 'string',
      label: '用户在线状态',
    },
    online_state_updated_at: {
      description: '在线状态更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '在线状态更新时间',
    },
    device_id: {
      description: '当前登录设备ID（关联 device_info.id）',
      format: 'string',
      type: 'string',
      label: '当前登录设备ID',
    },
    stream_sync_status: {
      description: '同步状态（可选值：pending=待同步; synced=已同步; failed=同步失败）',
      format: 'cons.StreamSyncStatus',
      type: 'string',
      label: '同步状态',
    },
    stream_sync_error: {
      description: 'Stream.io同步错误信息',
      format: 'string',
      type: 'string',
      label: 'Stream.io同步错误信息',
    },
    stream_synced_at: {
      description: 'Stream.io同步时间',
      format: '*time.Time',
      type: 'string',
      label: 'Stream.io同步时间',
    },
  },
  type: 'object',
}
